import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AUTH_ERROR_CODES,
  errorCode,
  errorStatus,
  toApiError
} from '@pharvio/core/shared';
import { Subject, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ClearStepUp } from './step-up.actions';
import { StepUpCancelledError } from './step-up-cancelled.error';
import { StepUpService } from './step-up.service';
import { serialiseStepUpPayload } from './step-up.state';

/** Step-up endpoints — skip intercepting their own responses */
const STEP_UP_PATHS = ['/auth/step-up/'];

function isStepUpRequest(url: string): boolean {
  return STEP_UP_PATHS.some((path) => url.includes(path));
}

/** Stable key for deduplicating concurrent challenges */
function payloadKey(operation: string, payload: unknown): string {
  try {
    return `${operation}::${serialiseStepUpPayload(payload)}`;
  } catch {
    return `${operation}::__unserializable__`;
  }
}

interface PendingChallenge {
  token$: Subject<string>;
  cancel$: Subject<void>;
}

/**
 * In-flight challenge map — keyed by `operation::payload` so two concurrent
 * requests for the same operation + payload share one challenge.
 */
const pendingChallenges = new Map<string, PendingChallenge>();

/**
 * Step-up (OTP re-confirmation) for sensitive endpoints:
 * 1. A pre-flight token from `StepUpService.ensure()` is attached to the
 *    request whose body matches the challenged payload.
 * 2. Otherwise, a 403 `STEP_UP_REQUIRED` requests a challenge and — only if
 *    one was issued — opens the OTP dialog, then retries the ORIGINAL request
 *    once with `X-Step-Up-Token` and the same body object, so it serialises
 *    byte-identically. When no challenge can be issued the service explains
 *    why and the original 403 propagates.
 */
export const stepUpInterceptor: HttpInterceptorFn = (req, next) => {
  const stepUp = inject(StepUpService);
  const store = inject(Store);

  if (isStepUpRequest(req.url)) {
    return next(req);
  }

  if (req.method !== 'GET') {
    const preflightToken = stepUp.consumeTokenFor(req.body);
    if (preflightToken) {
      return next(cloneWithToken(req, preflightToken));
    }
  }

  return next(req).pipe(
    catchError((err: unknown) => {
      if (
        errorStatus(err) !== 403 ||
        errorCode(err) !== AUTH_ERROR_CODES.StepUpRequired
      ) {
        // EMAIL_VERIFICATION_REQUIRED and every other 403 surface to the caller.
        return throwError(() => err);
      }

      // The guard names the operation in `details`, so any @StepUp() endpoint
      // works without the FE knowing about it in advance.
      const details = toApiError(err).details;
      const operation =
        typeof details?.['operation'] === 'string'
          ? details['operation']
          : null;

      if (!operation) {
        // Can't request a challenge without knowing what to confirm.
        return throwError(() => err);
      }

      const key = payloadKey(operation, req.body);

      // Reuse an in-flight challenge for the same operation+payload
      const existing = pendingChallenges.get(key);
      if (existing) {
        return from(
          new Promise<string>((resolve, reject) => {
            existing.token$.subscribe({ next: resolve, error: reject });
            existing.cancel$.subscribe(() =>
              reject(new StepUpCancelledError())
            );
          })
        ).pipe(switchMap((token) => next(cloneWithToken(req, token))));
      }

      const pending: PendingChallenge = {
        token$: new Subject<string>(),
        cancel$: new Subject<void>()
      };
      pendingChallenges.set(key, pending);

      return from(stepUp.challenge(operation, req.body)).pipe(
        switchMap((token) => {
          pendingChallenges.delete(key);
          if (!token) {
            pending.cancel$.next();
            return throwError(() => new StepUpCancelledError());
          }
          // The dialog closes on the token; it's single-use and belongs to
          // this retry, so drop it from state before a pre-flight match.
          store.dispatch(new ClearStepUp());
          pending.token$.next(token);
          return next(cloneWithToken(req, token));
        }),
        catchError((dialogErr) => {
          pendingChallenges.delete(key);
          pending.cancel$.next();
          return throwError(() => dialogErr);
        })
      );
    })
  );
};

function cloneWithToken(
  req: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  return req.clone({ setHeaders: { 'X-Step-Up-Token': token } });
}
